import React, { Suspense, lazy, useEffect } from "react";
import { ParallaxProvider } from "react-scroll-parallax";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";

import GlobalLayout from "./layout/GlobalLayout";
import Header from "./layout/Header";
import Hero from "./components/home/Hero";
import HomeStats from "./components/home/HomeStats";
import ProductEngines from "./components/home/ProductEngines";
import WhyAepl from "./components/home/WhyAepl";
import IndustriesWeServe from "./components/common/IndustriesWeServe";
import ProofAndStandards from "./components/home/ProofAndStandards";
import CTASection from "./components/home/CTASection";
import { homeData } from "./data/homeData";
import Footer from "./layout/Footer";
import AssetPreloader from "./components/common/AssetPreloader";
import { assetPath } from "./utils/assetPath";
import SEOHead from "./components/common/SEOHead";
import CookieConsent from "./components/common/CookieConsent";
import { trackPageView } from "./utils/analytics";

/* ── Lazy-loaded route pages (code-split, loaded on demand) ── */
const Legal = lazy(() => import("./pages/Legal"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AboutUs = lazy(() => import("./pages/about/AboutUs"));
const LearnOn = lazy(() => import("./pages/platform/LearnOn"));
const Griffin = lazy(() => import("./pages/platform/Griffin"));
const Raptor = lazy(() => import("./pages/platform/Raptor"));

// New product pages
const AuroraProduct = lazy(() => import("./pages/platform/AuroraProduct"));
const CourseAI = lazy(() => import("./pages/platform/CourseAI"));

// Industry hub pages
const AviationHub = lazy(() => import("./pages/industry/aviation/AviationHub"));
const DefenceHub = lazy(() => import("./pages/industry/defence/DefenceHub"));
const EnergyHub = lazy(() => import("./pages/industry/energy/EnergyHub"));

// Industry sub-pages  Aviation
const FlightDeck = lazy(() => import("./pages/industry/aviation/FlightDeck"));
const Engineers = lazy(() => import("./pages/industry/aviation/Engineers"));
const CabinCrew = lazy(() => import("./pages/industry/aviation/CabinCrew"));

// Industry sub-pages  Defence
const AirForce = lazy(() => import("./pages/industry/defence/AirForce"));
const Navy = lazy(() => import("./pages/industry/defence/Navy"));
const Army = lazy(() => import("./pages/industry/defence/Army"));
const SpecialOps = lazy(() => import("./pages/industry/defence/SpecialOps"));

// About sub-pages
const Quest = lazy(() => import("./pages/about/Quest"));
const Leadership = lazy(() => import("./pages/about/Leadership"));
const Crew = lazy(() => import("./pages/about/Crew"));
const NotFound = lazy(() => import("./pages/NotFound"));

/* ── Premium loading fallback ── */
const PageLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-[#0a0f1c] gap-8">
    {/* Logo with Pulse Glow */}
    <div className="relative">
      <div className="absolute inset-0 bg-[#0084F9]/20 blur-3xl rounded-full animate-pulse" />
      <img
        src={assetPath("/AEPL_lOGO.svg")}
        alt="Loading..."
        className="h-16 w-auto relative z-10 opacity-90"
      />
    </div>

    {/* Animated Progress Bar */}
    <div className="w-48 h-[2px] bg-white/10 rounded-full overflow-hidden">
      <div
        className="h-full bg-linear-to-r from-transparent via-[#0084F9] to-transparent rounded-full animate-[shimmer_1.5s_ease-in-out_infinite]"
        style={{ width: "40%" }}
      />
    </div>
  </div>
);

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } else {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    // Track page view in GA4 (no-op if GA4 isn't initialized / consent not given)
    trackPageView(pathname);
  }, [pathname, hash]);
  return null;
};

const MainContent = () => (
  <>
    <SEOHead
      title="Anugraha Exceed | AI Training Intelligence Platform"
      description="Anugraha Exceed (AEPL) builds AI/ML & XR training intelligence for Aviation, Defence & Energy — trusted by Etihad, IndiGo, CAE, Alstom & the Indian Navy."
      path="/"
    />
    <Hero />
    <HomeStats />
    <WhyAepl />
    <ProductEngines />
    <IndustriesWeServe />
    <ProofAndStandards />

    <div className="relative bg-white">
      <CTASection
        title={homeData.closingCTA.heading}
        description={homeData.closingCTA.description}
        buttonText={homeData.closingCTA.primaryCTA}
        secondaryButtonText={homeData.closingCTA.secondaryCTA}
        microcopy={homeData.closingCTA.microcopy}
        downloadKey="home"
      />
    </div>
  </>
);

function App() {
  return (
    <Router>
      <ParallaxProvider>
        <GlobalLayout>
          <AssetPreloader />
          <Header />
          <ScrollToTop />
          <div className="grow flex flex-col">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Home */}
                <Route path="/" element={<MainContent />} />

                {/* Legal */}
                <Route path="/legal" element={<Legal />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/industries/aviation" element={<AviationHub />} />
                <Route
                  path="/industries/aviation/flight-deck"
                  element={<FlightDeck />}
                />
                <Route
                  path="/industries/aviation/engineers"
                  element={<Engineers />}
                />
                <Route
                  path="/industries/aviation/cabin-crew"
                  element={<CabinCrew />}
                />
                <Route path="/industries/defence" element={<DefenceHub />} />
                <Route
                  path="/industries/defence/air-force"
                  element={<AirForce />}
                />
                <Route path="/industries/defence/navy" element={<Navy />} />
                <Route path="/industries/defence/army" element={<Army />} />
                <Route
                  path="/industries/defence/special-operations"
                  element={<SpecialOps />}
                />
                <Route path="/industries/energy" element={<EnergyHub />} />

                {/* Products */}
                <Route path="/product/raptor" element={<Raptor />} />
                <Route path="/product/learnon" element={<LearnOn />} />
                <Route path="/product/aurora" element={<AuroraProduct />} />
                <Route path="/product/griffin" element={<Griffin />} />
                <Route path="/product/courseai" element={<CourseAI />} />

                {/* About */}
                <Route path="/about" element={<AboutUs />} />
                <Route path="/about/the-idea" element={<AboutUs />} />
                <Route path="/about/quest" element={<Quest />} />
                <Route path="/about/leadership" element={<Leadership />} />
                <Route path="/about/crew" element={<Crew />} />

                {/* Wildcard Fallback */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
          <Footer />
          <CookieConsent />
        </GlobalLayout>
      </ParallaxProvider>
    </Router>
  );
}

export default App;
